import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion, useIsPresent } from 'framer-motion';
import '../assets/styles/pickingSection.css';
import { useDeviceInfo } from '../lib/mobile';
import { useAnimation } from '../lib/AnimationContext';

const getStorageKey = (page, type) => `pickingData_${page}_${type}`;

/** "3.5/5 stars" ↔ 3.5 - the form a star answer takes in the QATA text. */
const starsText = (n) => `${n}/5 stars`;
const starsOf = (text) => {
    const m = String(text || '').match(/^(\d(?:\.5)?)\/5 stars$/);
    return m ? Number(m[1]) : 0;
};

/**
 * Five stars, half stars allowed: the left half of a star is .5, the right half the whole star.
 * Tapping the current rating clears it. `value`/`onChange` carry the saved text ("3.5/5 stars").
 */
function StarRating({ value, onChange }) {
    const [hover, setHover] = useState(0);
    const rating = starsOf(value);
    const shown = hover || rating;
    const pick = (n) => onChange(n === rating ? '' : starsText(n));
    return (
        <div className="starRating" onMouseLeave={() => setHover(0)} role="radiogroup">
            {[1, 2, 3, 4, 5].map((star) => {
                const fill = shown >= star ? 'full' : shown >= star - 0.5 ? 'half' : 'empty';
                return (
                    <span key={star} className={`star ${fill}`}>
                        ★
                        <button
                            type="button"
                            className="starHalf left"
                            aria-label={`${star - 0.5} stars`}
                            onMouseEnter={() => setHover(star - 0.5)}
                            onClick={() => pick(star - 0.5)}
                        />
                        <button
                            type="button"
                            className="starHalf right"
                            aria-label={`${star} stars`}
                            onMouseEnter={() => setHover(star)}
                            onClick={() => pick(star)}
                        />
                    </span>
                );
            })}
            <span className="starValue">{shown ? `${shown}/5` : ''}</span>
        </div>
    );
}

/**
 * The dimmed backdrop. While it's animating out it stops catching taps, so closing the box never
 * delays a scout's next button press.
 */
const PickingOverlay = React.forwardRef(function PickingOverlay({ children, preset }, ref) {
    const isPresent = useIsPresent();
    return (
        <motion.div
            className="pickingOverlay"
            ref={ref}
            style={{ pointerEvents: isPresent ? "auto" : "none" }}
            initial={preset ? { opacity: 0 } : false}
            animate={{ opacity: 1 }}
            exit={preset ? { opacity: 0 } : undefined}
            transition={preset ? preset.backdrop : { duration: 0 }}
        >
            <motion.div
                className="pickingContainer"
                initial={preset ? preset.modal.initial : false}
                animate={preset ? preset.modal.animate : undefined}
                exit={preset ? preset.modal.exit : undefined}
                transition={preset ? preset.modal.transition : { duration: 0 }}
            >
                {children}
            </motion.div>
        </motion.div>
    );
});

/**
 * The QATA popup.
 * mode "normal": auto/tele QATA for the current page, with Quick Questions.
 * mode "defense": a plain notes box for defense_qata (no quick questions exist for defense).
 */
function PickingSection({ visible, onClose, onDataSelect, currentQataValue, currentPage, mode = "normal", shouldAutoImport, gameSessionId, qataQuestions }) {
    const isDefense = mode === "defense";
    // Defense notes get their own sessionStorage slot so they never mix with the page's QATA state
    const storagePage = isDefense ? "Defense" : currentPage;
    const { preset } = useAnimation();
    const [selections, setSelections] = useState({});
    const [showQuestions, setShowQuestions] = useState(false);
    const [tempQataText, setTempQataText] = useState('');
    const [questionsText, setQuestionsText] = useState(''); // Separate textbox for imported questions
    const [importCount, setImportCount] = useState(0);
    const [hasImported, setHasImported] = useState(false);

    const { device, isTouch } = useDeviceInfo();
    const textareaRef = useRef(null);
    const overlayRef = useRef(null);

    // Determine which questions to use based on current page
    const questions = currentPage === "Auto" ? qataQuestions.auto : qataQuestions.tele;

    // Load saved selections when component becomes visible or page changes
    useEffect(() => {
        if (visible) {
        // Load saved questions text first
        const savedQuestionsText = sessionStorage.getItem(getStorageKey(storagePage, 'questionsText'));
        setQuestionsText(savedQuestionsText || '');
        
        // Extract just the notes portion from currentQataValue
        const currentValue = currentQataValue || '';
        let notesOnly = currentValue;
        
        if (savedQuestionsText && currentValue.startsWith(savedQuestionsText)) {
            // Remove the questions portion to get just the notes
            notesOnly = currentValue.substring(savedQuestionsText.length);
            if (notesOnly.startsWith('\n')) {
                notesOnly = notesOnly.substring(1);
            }
        }
        
        setTempQataText(notesOnly);
        
        // Always clear selections first to prevent cross-page contamination
        setSelections({});
        
        // Load saved selections for this specific page only
        const storageKey = getStorageKey(storagePage, 'selections');
        const savedSelections = sessionStorage.getItem(storageKey);
        
        if (savedSelections) {
            try {
            const parsedSelections = JSON.parse(savedSelections);
            // Additional validation: ensure we're loading selections for the correct page
            if (Object.keys(parsedSelections).length > 0) {
                setSelections(parsedSelections);
            }
            } catch (error) {
            console.error('Error parsing saved selections:', error);
            setSelections({});
            }
        }

        // Load last mode preference for this page
        const savedMode = sessionStorage.getItem(getStorageKey(storagePage, 'mode'));
        
        // Prioritize: 1) Last mode, 2) Screen size, 3) Touch (only for tablets), 4) Default text
        let shouldShowQuestions;
        if (isDefense) {
            // Defense is free-text only
            shouldShowQuestions = false;
        } else if (savedMode) {
            shouldShowQuestions = savedMode === 'questions';
        } else {
            // Screen size priority
            if (device === "mobile") {
            // Mobile phones always show questions
            shouldShowQuestions = true;
            } else if (device === "desktop") {
            // Laptops/larger screens always show text
            shouldShowQuestions = false;
            } else if (device === "tablet") {
            // Tablets/Chromebooks: check touch capability
            shouldShowQuestions = isTouch;
            } else {
            // Default fallback
            shouldShowQuestions = false;
            }
        }
        
        setShowQuestions(shouldShowQuestions);

        // Load import count for this page
        const savedImportCount = sessionStorage.getItem(getStorageKey(storagePage, 'importCount'));
        if (savedImportCount) {
            setImportCount(parseInt(savedImportCount) || 0);
        } else {
            setImportCount(0);
        }

        // Load import status for this page
        const savedImportStatus = sessionStorage.getItem(getStorageKey(storagePage, 'hasImported'));
        setHasImported(savedImportStatus === 'true');
        }
    }, [visible, currentQataValue, device, isTouch, currentPage, storagePage, isDefense, gameSessionId]); // Added gameSessionId to force reload

    // Reset state when game session changes (when starting new game)
    useEffect(() => {
        setSelections({});
        setHasImported(false);
        setImportCount(0);
        setQuestionsText('');
    }, [gameSessionId]);

    // Clear selections and questions text state when page changes to prevent cross-contamination
    useEffect(() => {
        setSelections({});
        // Clear state when page changes to ensure clean loading
        setQuestionsText('');
        setTempQataText('');
    }, [currentPage]); // not storagePage: switching QATA box while open is handled by the load effect above

    // Focus textarea when switching to text view
    useEffect(() => {
        if (!showQuestions && textareaRef.current && visible) {
        setTimeout(() => textareaRef.current?.focus(), 100);
        }
    }, [showQuestions, visible, storagePage]);

    const handleImport = useCallback(() => {
        // Ensure we only use selections that actually exist for the current page
        const hasValidSelections = Object.keys(selections).some(key => 
        selections[key] !== undefined && selections[key] !== ''
        );
        
        if (!hasValidSelections) {
        return; // Don't import if there are no valid selections for this page
        }
        
        const currentQuestions = currentPage === "Auto" ? qataQuestions.auto : qataQuestions.tele;
        
        // Validate that selection indices match available questions
        const validSelections = Object.entries(selections)
        .filter(([questionIndex, value]) => {
            const index = parseInt(questionIndex);
            const isValidIndex = index >= 0 && index < currentQuestions.length;
            const hasValue = value !== undefined && value !== '';
            return isValidIndex && hasValue;
        });
        
        if (validSelections.length === 0) {
        return; // No valid selections for current page
        }
        
        // Format the selections into a readable string, each on a new line with comma at end
        const formattedData = validSelections
        .map(([questionIndex, value]) => {
            const question = currentQuestions[parseInt(questionIndex)];
            return `${question.question} ${value},`;
        })
        .join('\n');

        if (formattedData) {
        // Put questions in the separate questions textbox, overriding previous content
        setQuestionsText(formattedData);
        sessionStorage.setItem(getStorageKey(storagePage, 'questionsText'), formattedData);
        
        // Combine both textboxes for final output (questions first, then notes)
        const combinedText = `${formattedData}${tempQataText ? '\n' + tempQataText : ''}`;
        onDataSelect(combinedText);
        }
        
        // Increment import count and save it
        const newImportCount = importCount + 1;
        setImportCount(newImportCount);
        sessionStorage.setItem(getStorageKey(storagePage, 'importCount'), newImportCount.toString());
        
        // Mark as imported and save the status
        setHasImported(true);
        sessionStorage.setItem(getStorageKey(storagePage, 'hasImported'), 'true');
        
        // Switch to text view to show the imported notes
        setShowQuestions(false);
        sessionStorage.setItem(getStorageKey(storagePage, 'mode'), 'text');
        
        // Keep selections - don't clear them anymore!
        
        // Don't close - stay open to show the text area with imported content
    }, [selections, currentPage, storagePage, tempQataText, onDataSelect, importCount]);

    // Auto-import when shouldAutoImport is true (only when leaving scouting page)
    useEffect(() => {
        if (shouldAutoImport && visible && !hasImported) {
        const hasSelections = Object.keys(selections).some(key => 
            selections[key] !== undefined && selections[key] !== ''
        );
        
        if (hasSelections) {
            // Format the selections into a readable string, each on a new line
            const currentQuestions = currentPage === "Auto" ? qataQuestions.auto : qataQuestions.tele;
            const formattedData = Object.entries(selections)
            .filter(([_, value]) => value !== undefined && value !== '')
            .map(([questionIndex, value]) => {
                const question = currentQuestions[parseInt(questionIndex)];
                if (!question) return '';
                return `${question.question} ${value},`;
            })
            .filter(text => text !== '')
            .join('\n');

            if (formattedData) {
            // Always override questions text for auto-import
            setQuestionsText(formattedData);
            sessionStorage.setItem(getStorageKey(storagePage, 'questionsText'), formattedData);
            
            // Combine both textboxes for final output (questions first, then notes)
            const combinedText = `${formattedData}${tempQataText ? '\n' + tempQataText : ''}`;
            onDataSelect(combinedText);
            }
            
            // Mark as imported for this auto-import
            setHasImported(true);
            sessionStorage.setItem(getStorageKey(storagePage, 'hasImported'), 'true');
            
            // Close after auto-import
            onClose();
        }
        }
    }, [shouldAutoImport, visible, selections, tempQataText, onDataSelect, onClose, currentPage, storagePage, hasImported]);

    // Simple close function for manual close (Alt, Esc, click outside)
    const handleClose = useCallback(() => {
        // Don't clear selections when manually closing - only clear after import
        onClose();
    }, [onClose]);

    // When closed, drop focus out of the (still briefly visible, animating-out) box so the next
    // scoring keypress goes to the scouting page instead of being typed into the notes.
    useEffect(() => {
        if (!visible && overlayRef.current && overlayRef.current.contains(document.activeElement)) {
            document.activeElement.blur();
        }
    }, [visible]);

    // Handle ESC to close, and click outside. Alt isn't handled here: Scout.js toggles/switches boxes on
    // Left Alt (normal QATA) vs Right Alt (defense QATA).
    useEffect(() => {
        const handleKeyDown = (e) => {
        if (visible && e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation(); // Prevent event from bubbling to Scout.js
            handleClose();
        }
        };

        const handleClickOutside = (e) => {
        if (visible && overlayRef.current === e.target) {
            handleClose();
        }
        };
        
        if (visible) {
        window.addEventListener('keydown', handleKeyDown, true); // Use capture phase to intercept early
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            window.removeEventListener('keydown', handleKeyDown, true);
            document.removeEventListener('mousedown', handleClickOutside);
        };
        }
    }, [visible, handleClose]);

    // Save selections whenever they change
    useEffect(() => {
        const storageKey = getStorageKey(storagePage, 'selections');
        if (Object.keys(selections).length > 0) {
        sessionStorage.setItem(storageKey, JSON.stringify(selections));
        } else {
        // Clear sessionStorage when no selections exist
        sessionStorage.removeItem(storageKey);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selections, currentPage]); // not storagePage: on a box switch the old selections would be saved under the new box's key

    const handleOptionSelect = (questionIndex, option) => {
        setSelections(prev => {
        const currentValue = prev[questionIndex];
        // If clicking the same option, unselect it (set to undefined)
        if (currentValue === option) {
            const newSelections = { ...prev };
            delete newSelections[questionIndex]; // Remove the key entirely
            return newSelections;
        } else {
            // Otherwise, select the new option
            return {
            ...prev,
            [questionIndex]: option
            };
        }
        });
    };

    const handleNumberInput = (questionIndex, value) => {
        setSelections(prev => {
        if (value === '' || value === null || value === undefined) {
            // If empty value, remove the selection entirely
            const newSelections = { ...prev };
            delete newSelections[questionIndex];
            return newSelections;
        } else {
            // Otherwise, set the value
            return {
            ...prev,
            [questionIndex]: value
            };
        }
        });
    };

    const handleTextareaChange = (e) => {
        const newValue = e.target.value;
        setTempQataText(newValue);
        
        // Combine questions text and regular text (questions first)
        const combinedText = questionsText ? `${questionsText}${newValue ? '\n' + newValue : ''}` : newValue;
        onDataSelect(combinedText);
    };

    const handleQuestionsTextChange = (e) => {
        const newValue = e.target.value;
        setQuestionsText(newValue);
        sessionStorage.setItem(getStorageKey(storagePage, 'questionsText'), newValue);
        
        // Combine questions text and regular text (questions first, then notes)
        const combinedText = newValue ? `${newValue}${tempQataText ? '\n' + tempQataText : ''}` : tempQataText;
        onDataSelect(combinedText);
    };

    const handleShowQuestions = () => {
        setShowQuestions(true);
        // Save mode preference
        sessionStorage.setItem(getStorageKey(storagePage, 'mode'), 'questions');
    };

    const handleBackToText = () => {
        setShowQuestions(false);
        // Save mode preference
        sessionStorage.setItem(getStorageKey(storagePage, 'mode'), 'text');
    };

    return (
        <AnimatePresence>
        {visible && (
        <PickingOverlay ref={overlayRef} preset={preset} key="qataOverlay">
            <div className={`pickingHeader${isDefense ? " pickingHeaderDefense" : ""}`}>
            <h3>{isDefense ? "Defense QATA" : `${currentPage} QATA`}</h3>
            <button className="pickingCloseBtn" onClick={handleClose}>×</button>
            </div>

            {showQuestions && !isDefense ? (
            // Questions view
            <>
                <div className="pickingContent">
                {questions.map((question, index) => (
                    <div key={index} className="pickingQuestion">
                    <h4 className="questionTitle">{question.question}</h4>
                    
                    {question.isStars ? (
                        <StarRating
                        value={selections[index]}
                        onChange={(stars) => handleNumberInput(index, stars)}
                        />
                    ) : question.isNumber ? (
                        <input
                        type="number"
                        className="pickingNumberInput"
                        placeholder="Enter time in seconds"
                        value={selections[index] || ''}
                        onChange={(e) => handleNumberInput(index, e.target.value)}
                        />
                    ) : question.isInput ? (
                        <input
                        type="text"
                        className="pickingNumberInput"
                        placeholder="Enter your response"
                        value={selections[index] || ''}
                        onChange={(e) => handleNumberInput(index, e.target.value)}
                        />
                    ) : (
                        <div className="pickingOptions">
                        {question.options.map((option) => (
                            <button
                            key={option}
                            className={`pickingOption ${selections[index] === option ? 'selected' : ''}`}
                            onClick={() => handleOptionSelect(index, option)}
                            >
                            {option}
                            </button>
                        ))}
                        </div>
                    )}
                    </div>
                ))}
                </div>
                
                <div className="pickingFooter">
                <button className="pickingBackBtn" onClick={handleBackToText}>
                    Back to Text
                </button>
                <button className="pickingImportBtn" onClick={handleImport}>
                    Import to Notes
                </button>
                </div>
            </>
            ) : (
            // Text editor view
            <>
                <div className="pickingTextContent">
                {questionsText ? (
                    // Two textboxes when questions have been imported
                    <div className={`pickingTextboxContainer ${device === 'mobile' || device === 'tablet' ? 'mobile-layout' : 'desktop-layout'}`}>
                    <div className="notesTextbox">
                        <textarea
                        ref={textareaRef}
                        className="pickingTextarea notesTextarea"
                        placeholder={`Type your ${currentPage} notes here...`}
                        value={tempQataText}
                        onFocus={(e) => {
                            const gojo = e.target;
                            gojo.setSelectionRange(gojo.value.length, gojo.value.length);
                        }}
                        onChange={handleTextareaChange}
                        />
                    </div>
                    <div className="questionsTextbox">
                        <textarea
                        className="pickingTextarea questionsTextarea"
                        placeholder={`Imported questions for ${currentPage}...`}
                        value={questionsText}
                        onChange={handleQuestionsTextChange}
                        />
                    </div>
                    </div>
                ) : (
                    // Single textbox when no questions imported
                    <textarea
                    ref={textareaRef}
                    className="pickingTextarea"
                    placeholder={isDefense
                        ? "How did they play defense? Who did they defend, push power, speed, penalties..."
                        : `Type your ${currentPage} notes here...`}
                    value={tempQataText}
                    onFocus={(e) => {
                        const gojo = e.target;
                        gojo.setSelectionRange(gojo.value.length, gojo.value.length);
                    }}
                    onChange={handleTextareaChange}
                    />
                )}
                </div>
                
                <div className="pickingFooter">
                {isDefense ? (
                    <button className="pickingQuestionsBtn" onClick={handleClose}>
                        Done
                    </button>
                ) : (
                    <button className="pickingQuestionsBtn" onClick={handleShowQuestions}>
                        Quick Questions
                    </button>
                )}
                </div>
            </>
            )}
        </PickingOverlay>
        )}
        </AnimatePresence>
    );
}

export default PickingSection;